/**
 * Viewport shorter than the mosaic floor (see MOSAIC_MIN_HEIGHT). Snap + fixed
 * stage with nested scroll fights itself here — stacked mode is the layout.
 *
 * Live `matchMedia` (not latched): the query is already desktop-only
 * (`min-width: 56rem`), so Android URL-bar height churn cannot flip it.
 * Latching height here left short desktop windows stuck in motion-on after a
 * height-only resize (ultrawide / short browser chrome).
 */

import { useSyncExternalStore } from "react";

import { HUB_SHORT_VIEWPORT_QUERY } from "@story/data-centers-hub/config/constants";

function subscribe(onStoreChange: () => void): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }
  const mediaQuery = window.matchMedia(HUB_SHORT_VIEWPORT_QUERY);
  mediaQuery.addEventListener("change", onStoreChange);
  // Height-only window resizes do not always fire MQ `change` when the
  // threshold is in `svw` — re-check on resize so short desktop windows
  // still flip into stacked mode.
  window.addEventListener("resize", onStoreChange);
  return () => {
    mediaQuery.removeEventListener("change", onStoreChange);
    window.removeEventListener("resize", onStoreChange);
  };
}

function getSnapshot(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.matchMedia(HUB_SHORT_VIEWPORT_QUERY).matches;
}

export function useHubShortViewport(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
